import { useState, useMemo } from "react";
import { 
  Radio, 
  CheckCircle2, 
  Trash2, 
  Folder, 
  FolderOpen, 
  ChevronRight, 
  ChevronDown 
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { BRAND_COLORS } from "./CascadingEquipmentSelector";

/**
 * Equipment By Brand View Component
 * Legacy folder-based view for equipment selection
 */
export function EquipmentByBrandView({ 
  equipmentList, 
  selectedEquipmentId, 
  onSelect, 
  user, 
  onDelete 
}) {
  const [expandedBrands, setExpandedBrands] = useState({});

  // Group equipment by brand
  const groupedEquipment = useMemo(() => {
    const groups = {};
    
    equipmentList.forEach(eq => {
      const brand = eq.brand || "Otros";
      if (!groups[brand]) {
        groups[brand] = [];
      }
      groups[brand].push(eq);
    });
    
    // Sort brands alphabetically, but put "Otros" last
    const sortedBrands = Object.keys(groups).sort((a, b) => {
      if (a === "Otros") return 1;
      if (b === "Otros") return -1;
      return a.localeCompare(b);
    });
    
    const sorted = {};
    sortedBrands.forEach(brand => {
      sorted[brand] = groups[brand];
    });
    
    return sorted;
  }, [equipmentList]);

  const toggleBrand = (brand) => {
    setExpandedBrands(prev => ({ ...prev, [brand]: !prev[brand] }));
  };

  return (
    <div className="space-y-2">
      {Object.entries(groupedEquipment).map(([brand, equipment]) => {
        const brandExpanded = expandedBrands[brand] === true;
        const brandColor = BRAND_COLORS[brand] || BRAND_COLORS["Otros"];
        const hasSelectedInBrand = equipment.some(eq => eq.id === selectedEquipmentId);
        
        return (
          <div 
            key={brand} 
            className={`bg-[#111111] border rounded-sm overflow-hidden transition-all ${
              hasSelectedInBrand ? "border-[#34C759]/50" : "border-white/[0.08]"
            }`}
          >
            {/* Brand Header */}
            <button
              onClick={() => toggleBrand(brand)}
              className="w-full flex items-center justify-between p-3 hover:bg-white/5 transition-colors"
            >
              <div className="flex items-center gap-2">
                {brandExpanded ? (
                  <FolderOpen className="w-5 h-5" style={{ color: brandColor }} />
                ) : (
                  <Folder className="w-5 h-5" style={{ color: brandColor }} />
                )}
                <span className="font-semibold text-[#F4F4F5]">{brand}</span>
                <span className="text-xs text-[#71717A] bg-white/5 px-2 py-0.5 rounded">
                  {equipment.length} equipo{equipment.length !== 1 ? "s" : ""}
                </span>
              </div>
              <div className="flex items-center gap-2">
                {hasSelectedInBrand && (
                  <span className="text-xs text-[#34C759]">● Seleccionado</span>
                )}
                {brandExpanded ? (
                  <ChevronDown className="w-4 h-4 text-[#71717A]" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-[#71717A]" />
                )}
              </div>
            </button>
            
            {/* Equipment within brand */}
            {brandExpanded && (
              <div className="border-t border-white/[0.05] p-2">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {equipment.map((eq) => (
                    <div
                      key={eq.id}
                      onClick={() => onSelect(eq.id)}
                      className={`p-3 rounded-sm border cursor-pointer transition-all ${
                        selectedEquipmentId === eq.id
                          ? "bg-[#34C759]/10 border-[#34C759]"
                          : "bg-[#0A0A0A] border-white/[0.05] hover:border-white/20"
                      }`}
                      data-testid={`equipment-card-${eq.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Radio className={`w-4 h-4 ${selectedEquipmentId === eq.id ? "text-[#34C759]" : "text-[#71717A]"}`} />
                          <div>
                            <p className={`font-medium text-sm ${selectedEquipmentId === eq.id ? "text-[#F4F4F5]" : "text-[#A1A1AA]"}`}>
                              {eq.model}
                            </p>
                            <p className="text-[10px] text-[#71717A]">
                              Máx {eq.max_systems} sistemas
                            </p>
                          </div>
                        </div>
                        {selectedEquipmentId === eq.id && (
                          <CheckCircle2 className="w-4 h-4 text-[#34C759]" />
                        )}
                      </div>
                      {/* Show delete button for admin on manually added equipment */}
                      {user?.role === "admin" && eq.is_global && eq.id?.startsWith("manual_") && (
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            onDelete(eq.id);
                          }}
                          variant="ghost"
                          size="sm"
                          className="mt-2 w-full text-red-500 hover:text-red-400 hover:bg-red-500/10 h-7 text-xs"
                        >
                          <Trash2 className="w-3 h-3 mr-1" />
                          Eliminar
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default EquipmentByBrandView;
